
import React, { useState, useEffect } from "react";
import { BottomNavigation } from "@/components/navigation/BottomNavigation";
import { User } from "@/lib/auth";
import { WorkerHeader } from "../calendar/worker/WorkerHeader";
import { WeeklyCalendarView } from "./WeeklyCalendarView";
import { WorksiteSummary } from "./WorksiteSummary";
import { DailyTimeEntries } from "./DailyTimeEntries";
import { getUserTimeEntries, TimeEntry } from "@/services/timeEntryService";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfWeek, addDays } from "date-fns";
import { fr } from "date-fns/locale";

interface WeeklySummaryWorkerProps {
  user: User;
}

interface WorksiteInfo {
  id: string;
  name: string;
}

interface TimeEntryWithWorksite extends Omit<TimeEntry, 'worksite_id'> {
  worksiteId: string;
  worksiteName: string;
  clockIn: string;
  clockOut: string;
}

interface WorksiteSummaryData {
  worksiteId: string;
  worksiteName: string;
  totalHours: number;
}

export const WeeklySummaryWorker: React.FC<WeeklySummaryWorkerProps> = ({ user }) => {
  const [timeEntries, setTimeEntries] = useState<TimeEntryWithWorksite[]>([]);
  const [worksiteSummaries, setWorksiteSummaries] = useState<WorksiteSummaryData[]>([]);
  const [worksites, setWorksites] = useState<WorksiteInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // Calculate current week days
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 }); // Start on Monday
  const daysOfWeek = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const startDate = format(weekStart, 'yyyy-MM-dd');
  const endDate = format(addDays(weekStart, 6), 'yyyy-MM-dd');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch worksites
        const { data: worksitesData, error: worksitesError } = await supabase
          .from('worksites')
          .select('id, name');
        
        if (worksitesError) {
          console.error('Error fetching worksites:', worksitesError);
          return;
        }
        
        setWorksites(worksitesData || []);

        // Fetch time entries for current week
        const entries = await getUserTimeEntries(user.id, startDate, endDate);
        
        // Transform entries to match expected format
        const transformedEntries: TimeEntryWithWorksite[] = entries
          .filter(entry => entry.end_time) // Only completed entries
          .map(entry => {
            const worksite = worksitesData?.find(w => w.id === entry.worksite_id);
            return {
              id: entry.id,
              date: entry.date,
              start_time: entry.start_time,
              end_time: entry.end_time,
              worksiteId: entry.worksite_id,
              worksiteName: worksite?.name || 'Chantier inconnu',
              clockIn: entry.start_time,
              clockOut: entry.end_time || '',
              user_id: entry.user_id,
              start_coordinates: entry.start_coordinates,
              end_coordinates: entry.end_coordinates,
              comment: entry.comment,
              breaks: entry.breaks,
              status: entry.status,
              created_at: entry.created_at,
              updated_at: entry.updated_at
            };
          });

        setTimeEntries(transformedEntries);

        // Calculate worksite summaries
        const summaryMap = new Map<string, { name: string; hours: number }>();
        
        transformedEntries.forEach(entry => {
          if (entry.clockIn && entry.clockOut) {
            const [startHour, startMin] = entry.clockIn.split(':').map(Number);
            const [endHour, endMin] = entry.clockOut.split(':').map(Number);
            
            const startTime = startHour + startMin / 60;
            const endTime = endHour + endMin / 60;
            const hours = endTime - startTime;
            
            const existing = summaryMap.get(entry.worksiteId);
            if (existing) {
              existing.hours += hours;
            } else {
              summaryMap.set(entry.worksiteId, {
                name: entry.worksiteName,
                hours: hours
              });
            }
          }
        });

        const summaries: WorksiteSummaryData[] = Array.from(summaryMap.entries()).map(([id, data]) => ({
          worksiteId: id,
          worksiteName: data.name,
          totalHours: data.hours
        }));

        setWorksiteSummaries(summaries);
      } catch (error) {
        console.error('Error fetching weekly summary data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.id, startDate, endDate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#BD1E28] border-e-transparent mb-4"></div>
          <p className="text-gray-600">Chargement du suivi hebdomadaire...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8] flex flex-col">
      {/* Header with title */}
      <WorkerHeader title="Suivi Hebdomadaire" />

      {/* Main Content */}
      <main className="flex-1 mt-16 mb-16 px-4 pb-4 space-y-6">
        {/* Weekly Calendar View */}
        <WeeklyCalendarView timeEntries={timeEntries} daysOfWeek={daysOfWeek} />

        {/* Worksite Summary */}
        <WorksiteSummary worksiteSummaries={worksiteSummaries} />

        {/* Daily Time Entries */}
        <DailyTimeEntries timeEntries={timeEntries} daysOfWeek={daysOfWeek} />
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation activeTab="suivi-hebdo" />
    </div>
  );
};
